/**
 * The icon set: animated SVG icons from @svg-animated-icons/react, keyed by
 * what they mean in this UI rather than by glyph, so swapping a glyph is a
 * one-line change here. Each icon is a 1em currentColor SVG. The library's
 * hover animation is disabled; instead the animation runs when the enclosing
 * button or pill is clicked (see the `.icon-play` handler in main.tsx and
 * theme/icon-hover.css).
 */

import {
  ActivityLogIcon,
  ArchiveIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  CheckboxIcon,
  CheckCircledIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CodeIcon,
  CommitIcon,
  CounterClockwiseClockIcon,
  Cross2Icon,
  CrossCircledIcon,
  DashboardIcon,
  DesktopIcon,
  DotsVerticalIcon,
  DownloadIcon,
  ExclamationCircledIcon,
  ExclamationTriangleIcon,
  ExitIcon,
  FilePlusIcon,
  FileTextIcon,
  GearIcon,
  GlobeIcon,
  HomeIcon,
  InfoCircledIcon,
  LightningBoltIcon,
  Link1Icon,
  Link2Icon,
  LinkBreak2Icon,
  LockClosedIcon,
  MagicWandIcon,
  MagnifyingGlassIcon,
  MinusCircledIcon,
  MoonIcon,
  PanelRightIcon,
  Pencil2Icon,
  PlayIcon,
  PlusIcon,
  QuestionMarkCircledIcon,
  ReloadIcon,
  RocketIcon,
  Share1Icon,
  SlashIcon,
  StopIcon,
  SunIcon,
  TrashIcon,
  ViewGridIcon,
} from '@svg-animated-icons/react'
import { CanopyLogo } from '@web/components/CanopyLogo'
import type { ComponentType } from 'react'

type LibIcon = ComponentType<{ disableHover?: boolean; className?: string }>

function BrainIcon() {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5a3 3 0 0 0-5.8-1A4 4 0 0 0 3 10.5a4.5 4.5 0 0 0 2 8 3.5 3.5 0 0 0 7 0V5Zm0 0a3 3 0 0 1 5.8-1 4 4 0 0 1 3.2 6.5 4.5 4.5 0 0 1-2 8 3.5 3.5 0 0 1-7 0" />
      <path d="M7 4v3m10-3v3M3 11h3l2 2m13-2h-3l-2 2M5 18.5V16m14 2.5V16M8 9l4 2 4-2M8 17l4-2 4 2" />
    </svg>
  )
}

const ICONS = {
  plus: PlusIcon,
  refresh: ReloadIcon,
  close: Cross2Icon,
  chevronDown: ChevronDownIcon,
  chevronRight: ChevronRightIcon,
  check: CheckIcon,
  receipt: CheckIcon,
  trash: TrashIcon,
  panelRight: PanelRightIcon,
  folder: ArchiveIcon,
  repo: CommitIcon,
  clone: DownloadIcon,
  activity: ActivityLogIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationCircledIcon,
  thinking: BrainIcon,
  gauge: DashboardIcon,
  fork: Share1Icon,
  play: PlayIcon,
  end: ExitIcon,
  stop: StopIcon,
  online: Link2Icon,
  offline: LinkBreak2Icon,
  clock: ClockIcon,
  archive: ArchiveIcon,
  fileText: FileTextIcon,
  attach: Link1Icon,
  send: ArrowUpIcon,
  slash: SlashIcon,
  zap: LightningBoltIcon,
  arrowDown: ArrowDownIcon,
  rewind: CounterClockwiseClockIcon,
  terminal: CodeIcon,
  noResult: MinusCircledIcon,
  shieldAlert: LockClosedIcon,
  shieldX: CrossCircledIcon,
  shieldCheck: CheckCircledIcon,
  question: QuestionMarkCircledIcon,
  agent: RocketIcon,
  edit: Pencil2Icon,
  write: FilePlusIcon,
  search: MagnifyingGlassIcon,
  web: GlobeIcon,
  todo: CheckboxIcon,
  tool: GearIcon,
  magic: MagicWandIcon,
  // Mobile chrome: the flat header, sheets and settings screen.
  chevronLeft: ChevronLeftIcon,
  arrowLeft: ArrowLeftIcon,
  more: DotsVerticalIcon,
  grid: ViewGridIcon,
  home: HomeIcon,
  settings: GearIcon,
  info: InfoCircledIcon,
  sun: SunIcon,
  moon: MoonIcon,
  desktop: DesktopIcon,
} satisfies Record<string, LibIcon>

export type IconName = keyof typeof ICONS | 'loader'

export function Icon({
  name,
  size = 14,
  className,
  still,
  title,
}: {
  name: IconName
  /** Pixel size; the SVG is 1em. */
  size?: number
  /** Applied to the wrapper, so colour and animation classes (text-ok, spin) work. */
  className?: string
  /** Never animate, not even on click. */
  still?: boolean
  title?: string
}) {
  if (name === 'loader') {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center leading-none ${(
          className ?? ''
        )
          .split(/\s+/)
          .filter((value) => value !== 'spin')
          .join(' ')}`}
        title={title}
        aria-hidden={title ? undefined : true}
      >
        <CanopyLogo size={size} animated={!still} transparent />
      </span>
    )
  }
  const C = ICONS[name]
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center leading-none ${still ? 'icon-still' : ''} ${className ?? ''}`}
      style={{ fontSize: size }}
      title={title}
      aria-hidden={title ? undefined : true}
    >
      <C disableHover className="cursor-[inherit]" />
    </span>
  )
}
